import { setModalWithData, useAppConfigurationSelector, useModalSelector, useTranslate } from "front";
import { translations } from "../../../../translations";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppDispatch, ReduxStoreState } from "redux/store";
import { SubmitHandler, useForm } from "react-hook-form";
import { useGroupsCache } from "../../../../hooks/useQuery/useGroupsCache";
import { useUsersCache } from "../../../../hooks/useQuery/useUsersCache";
import { Avatar, DropdownProps, Flex, InputProps, Text } from "@fluentui/react-northstar";
import { useParticipantsCache } from "../../../../hooks/useQuery/useParticipantsCache";
import { IContact } from "../../../../interfaces/IContact/IContact";
import { queryClient } from "../../../../index";
import { QUERY_KEY_ALL_USERS, QUERY_KEY_PARTICIPANTS, QUERY_KEY_PARTICIPANTS_FOR_GROUPS_BY_SEARCH, QUERY_KEY_USERS } from "../../../../const/queryKey";
import { IGroup } from "../../../../interfaces/IGroup/IGroup";
import { COUNTER_BEFORE_SEARCH } from "../../../../const/const";
import { searchAllContacts } from "../../../../apis/actions/contactAction";
import { useContactListCache } from "../../../../hooks/useQuery/useContactListCache";
import { parseGroupIds } from "../../../../utils/parseGroupIds";

export const useGroup = () => {
  const t = useTranslate(translations);
  const dispatchCtx = useDispatch<IAppDispatch>();

  // Selector
  const { data } = useModalSelector("data");
  const { appId } = useAppConfigurationSelector("appId");
  const pagination = useSelector((s: ReduxStoreState) => s.pagination);

  // Caches
  const { queryGroups, mutateCreateGroup, mutateEditGroup } = useGroupsCache();
  const { queryAllUsers } = useUsersCache();
  const { queryParticipants } = useParticipantsCache(data.group?.id, false);
  const { queryContactList } = useContactListCache();

  // States
  const [participants, setParticipants] = useState<IContact[]>([]);
  const [currentSelectedParticipants, setCurrentSelectedParticipants] = useState<IContact[]>([]);
  const [dropdownValue, setDropdownValue] = useState<any>([]);
  const [bannerIsOpen, setBannerIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<IContact[]>([]);

  const allContacts = queryAllUsers.data?.contacts ?? [];

  useEffect(() => {
    if (data.isEditing) return;
    setTimeout(() => {
      trigger("name", { shouldFocus: true });
    }, 10);
  }, [data.isEditing]);

  useEffect(() => {
    if (!queryParticipants.data) return;
    setParticipants(queryParticipants.data);
  }, [queryParticipants.data]);

  const getGroup = useMemo(() => {
    if (!queryGroups.data || !data.group?.id) return undefined;
    return queryGroups.data.find((g) => g.id === data.group.id);
  }, [queryGroups.data, data.group?.id]);

  const {
    formState: { errors, isValid, isDirty },
    control,
    trigger,
    handleSubmit,
  } = useForm<{ name: string; participants: { add: string[]; remove: string[] } }>({
    mode: "onChange",
    defaultValues: {
      name: getGroup?.name ?? "",
      participants: {
        add: [],
        remove: [],
      },
    },
  });

  const handleClose = useCallback(() => dispatchCtx(setModalWithData({ isOpen: undefined, data: undefined })), []);

  useEffect(() => {
    if (!search) return;
    const timeout = setTimeout(() => {
      onSearchContact();
    }, COUNTER_BEFORE_SEARCH);
    return () => clearTimeout(timeout);
  }, [search]);

  const onSearchContact = async () => {
    if (!queryContactList.data) return;
    const params = { contactListId: queryContactList.data.id, search };
    const searchedContacts = await searchAllContacts(params);
    setContacts([...(searchedContacts?.result ?? [])]);
  };

  const getAvailableContacts = useCallback((): IContact[] => {
    const merged = [...allContacts];
    contacts.forEach((c) => {
      if (!merged.some((m) => m.id === c.id)) merged.push(c);
    });
    currentSelectedParticipants.forEach((c) => {
      if (!merged.some((m) => m.id === c.id)) merged.push(c);
    });
    return merged;
  }, [allContacts, contacts, currentSelectedParticipants]);

  // Helper function to resolve the contact id from the dropdown value
  const resolveContactId = (item: unknown, availableContacts: IContact[]): string | undefined => {
    if (item == null) return undefined;
    if (typeof item === "string") {
      return availableContacts.find((c) => c.name === item)?.id;
    }
    if (typeof item === "object") {
      const entry = item as { id?: string; header?: string };
      if (entry.id) return availableContacts.find((c) => c.id === entry.id)?.id ?? entry.id;
      if (entry.header) return availableContacts.find((c) => c.name === entry.header)?.id;
    }
    return undefined;
  };

  const handleSearch = (_: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null, data: DropdownProps) => {
    setSearch(data.searchQuery ?? "");
  };

  const updateCurrentParticipants = (ids: string[], availableContacts: IContact[]) => {
    setCurrentSelectedParticipants(availableContacts.filter((p) => ids.includes(p.id)));
  };

  const handleDropdown = (_: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null, data: DropdownProps, field: any) => {
    if (!queryAllUsers.data || data.value == null) return;
    const valueArray = Array.isArray(data.value) ? data.value : [data.value];
    const availableContacts = getAvailableContacts();
    const ids = valueArray.map((item) => resolveContactId(item, availableContacts)).filter((id): id is string => Boolean(id));
    field.value.add = ids;
    field.value.remove = field.value.remove.filter((id: string) => !ids.includes(id));
    field.onChange(field.value);
    setDropdownValue(valueArray);
    updateCurrentParticipants(ids, availableContacts);
  };

  const handleRemove = (id: string, field: any) => {
    if (!queryAllUsers.data) return;
    const wasInitialParticipant = queryParticipants.data?.some((p) => p.id === id) ?? false;
    field.value.add = field.value.add.filter((participantId: string) => participantId !== id);
    field.value.remove = field.value.remove.filter((participantId: string) => participantId !== id);
    if (wasInitialParticipant) field.value.remove.push(id);
    field.onChange(field.value);
    setParticipants((prev) => prev.filter((p) => p.id !== id));
    const currentIds = currentSelectedParticipants.map((p) => p.id);
    const filteredIds = currentIds.filter((p) => p !== id);
    updateCurrentParticipants(filteredIds, getAvailableContacts());
    setDropdownValue((prev: any[]) => {
      const target = prev.map((p) => {
        if (typeof p === "string") {
          const contact = allContacts.find((i) => i.name === p);
          return contact?.id === id ? undefined : p;
        } else {
          return p.id === id ? undefined : p;
        }
      });
      const filteredTarget = target.filter((val) => val);
      return filteredTarget;
    });
  };

  const onSubmit: SubmitHandler<{ name: string; participants: { add: string[]; remove: string[] } }> = useCallback(
    async (formData: { name: string; participants: { add: string[]; remove: string[] } }) => {
      try {
        const participants = {
          add: formData.participants.add.filter(Boolean),
          remove: formData.participants.remove.filter(Boolean),
        };
        const hasChanges = participants.add.length !== 0 || participants.remove.length !== 0;

        if (data.isEditing) {
          const params = { ...data.group, name: formData.name, participants, contactListId: appId };
          await mutateEditGroup.mutateAsync(params, { onSuccess: () => updateUser(data.group.id, participants) });
        } else {
          const params = { name: formData.name, participants, contactListId: appId };
          await mutateCreateGroup.mutateAsync(params, {
            onSuccess: (res: IGroup) => {
              // TODO: make useUsersCache normalize the response so we can handle the response correctly
              const groupId = res?.id ?? (res as { result?: IGroup })?.result?.id;
              if (groupId) updateUser(groupId, participants);
            },
          });
        }
        if (hasChanges) {
          queryClient.removeQueries(QUERY_KEY_PARTICIPANTS);
          queryClient.removeQueries(QUERY_KEY_PARTICIPANTS_FOR_GROUPS_BY_SEARCH);
          queryClient.removeQueries(QUERY_KEY_USERS);
        }
      } catch (error) {
        console.error(error);
      } finally {
        handleClose();
      }
    },
    []
  );

  const updateUser = (groupId: string, participants: { add: string[]; remove: string[] }) => {
    queryClient.setQueriesData([QUERY_KEY_ALL_USERS, appId], (oldData: { total: number; contacts: IContact[] } | undefined) => {
      if (!oldData) return { total: 0, contacts: [] };
      var updatedContacts = oldData.contacts.map((i) => (participants.add.includes(i.id) ? updateUserGroup(i, groupId) : i));
      return {
        ...oldData,
        contacts: updatedContacts,
      };
    });
  };

  const updateUserGroup = (contact: IContact, groupId: string): IContact => {
    const initialGroups = contact.groupId ? parseGroupIds(contact.groupId) : [];
    if (initialGroups.includes(groupId)) return contact;
    return { ...contact, groupId: [...initialGroups, groupId].join(", ") };
  };

  const handleInput = (
    _: unknown,
    data:
      | (InputProps & {
          value: string;
        })
      | undefined
  ) => {
    if (typeof data?.value !== "string") return;
    const filteredParticipants = queryParticipants.data?.filter((p) => p.name.toLocaleLowerCase().includes(data.value.toLocaleLowerCase())) ?? [];
    setParticipants(filteredParticipants);
  };

  const handleInputValidation = (value: string) => {
    if (data.group) {
      return !queryGroups.data.find((g) => g.name.toLocaleLowerCase() === value.toLocaleLowerCase() && g.id !== data.group.id);
    } else {
      return !queryGroups.data.find((g) => g.name.toLocaleLowerCase() === value.toLocaleLowerCase());
    }
  };

  const handleDropdownItems = () => {
    const selectedIds = data.isEditing ? participants.map((p) => p.id) : currentSelectedParticipants.map((p) => p.id);
    if (!contacts || contacts.length === 0)
      return allContacts
        .filter((c) => !selectedIds.includes(c.id))
        .map((c, idx) => ({
          id: c.id,
          header: c.name,
          key: idx,
          content: (
            <Flex key={c.id} hAlign="start" vAlign="center" gap="gap.small">
              <Avatar image={c.picture} name={c.name.toUpperCase()} size="smallest" />
              <Text content={c.name} />
            </Flex>
          ),
        }));
    const filteredContact = contacts.filter((qu) => !selectedIds.includes(qu.id)) ?? [];
    const items = filteredContact.map((c, idx) => ({
      id: c.id,
      header: c.name,
      key: idx,
      content: (
        <>
          <Flex key={c.id} hAlign="start" vAlign="center" gap="gap.small">
            <Avatar image={c.picture} name={c.name.toUpperCase()} size="smallest" />
            <Text content={c.name} />
          </Flex>
        </>
      ),
    }));

    return items;
  };

  const closeBanner = useCallback(() => setBannerIsOpen(false), []);

  const openBanner = useCallback(() => {
    if (!isDirty)
    {
      handleClose();
    }
    setBannerIsOpen(true)
  }, [isDirty]);

  return {
    t,
    handleClose,
    isEditing: data.isEditing,
    errors,
    control,
    isLoading: mutateCreateGroup.isLoading || mutateEditGroup.isLoading,
    handleSubmit,
    onSubmit,
    isValid,
    isDirty,
    contacts: queryAllUsers.data?.contacts ?? [],
    handleSearch,
    participants,
    handleDropdown,
    handleRemove,
    handleInput,
    handleInputValidation,
    groups: queryGroups.data,
    handleDropdownItems,
    currentSelectedParticipants,
    dropdownValue,
    setDropdownValue,
    closeBanner,
    openBanner,
    bannerIsOpen,
    searchedContacts: contacts
  };
};
